import { CoordinatorService, MANIFEST_STORE_DELAY } from './coordinator.service';
import { ScriptHitset } from './script-hitset';
import { Script } from './script';
import { STG } from './stage';
import { ScriptSubset } from './script-subset';
import { ScriptKeyword } from './script-keyword';
import { KeywordGroup } from './keyword-group';
import { StyleGroup } from './style-group';
import { ScriptKeywordGroup } from './script-keyword-group';
import { BTPAR, BTTYPE } from './binary-tree';
import { Subject, debounceTime } from 'rxjs';
import { ScriptSettings } from './script-settings';
import { Attachment } from './attachment';
import { ScriptContext } from './script-context';
import { Temporal } from '@js-temporal/polyfill';
import { ScriptWidget } from './script-widget';
import { SearchStringParser } from './search-string-parser';
import { ScriptXml } from './script-xml';
import { SearchNumberParser } from './search-number-parser';
import { ScriptCitation } from './script-citation';
import { ScriptHtml } from './script-html';
import { ScriptInterface } from './script-interface';
import { ScriptSearchArchive } from './script-search-archive';
import { ScriptInput } from './script-input';
import { ScriptOutput } from './script-output';
import { ScriptsSystem$ } from './scripts-system$';
import { Report } from './report';
import { ScriptBatchManager } from './script-batch-manager';

export class Script$ {
    private _widgetMap = new Map<string, ScriptWidget>();
    system$: ScriptsSystem$;

    markForRun$ = new Subject<void>();
    private _markForCheck$ = new Subject<void>();
    markForCheck$ = this._markForCheck$.pipe(debounceTime(1000));

    private _hasScriptInRun = false;

    private _availableFunctions: any = {};
    private _scriptMap = new Map<string, Script>();

    private _markForManifestStoreSubject$ = new Subject<void>();
    private _markForManifestStore$ = this._markForManifestStoreSubject$.pipe(
        debounceTime(MANIFEST_STORE_DELAY),
    );

    inItemDetailActiveLs: Script[] = [];
    inItemListActiveLs: Script[] = [];
    isConnectorLs: Script[] = [];
    isSummaryLs: Script[] = [];

    constructor(public coords: CoordinatorService) {
        this._prepareContext();
        this.openProject.bind(this);
        this.stage.bind(this);
        this.hitset.bind(this);
        this.prompt.bind(this);

        this._markForManifestStore$.subscribe(() => this._manifestStore());
        this.system$ = new ScriptsSystem$(this.coords);
    }

    async widgetRegistration(w: any) {
        await this.coords.utils$.wait(0.1);
        const sw = new ScriptWidget(this.coords, w);
        this._widgetMap.set(sw.id, sw);
        // console.log('Widget registered:', sw.id);
    }

    get project() {
        return this.coords.project$.current?.scriptClass;
    }

    widgetDeregistration(w: any) {
        this._widgetMap.delete(w.scriptId);
    }

    widgetGet(widgetId: string) {
        return this._widgetMap.get(widgetId);
    }

    markForCheck() {
        if (this.hasScriptInRun) return;
        this._markForCheck$.next();
    }
    markForManifestStore() {
        this._markForManifestStoreSubject$.next();
    }
    onStart() {}

    clear() {
        this._scriptMap.clear();
    }

    dialog(params?: Object) {
        this.coords.display$.dialog$.next(
            Object.assign(
                {
                    action: 'scripts',
                },
                params || {},
            ),
        );
    }

    refreshInLists() {
        const ls = this.scriptLs;
        ls.sort((a, b) => a.weight - b.weight);
        this.inItemDetailActiveLs = ls.filter((x) => x.inItemDetail);
        this.inItemListActiveLs = ls.filter((x) => x.inItemList);
        this.isConnectorLs = ls.filter((x) => x.isConnector);
        this.isSummaryLs = ls.filter((x) => x.isSummary);
    }

    get scriptLs(): Script[] {
        const ls = Array.from(this._scriptMap.values());
        ls.sort((a, b) => (a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1));
        return ls;
    }

    load(params: any) {
        console.log('load script', params);
        const s = new Script(this.coords, {
            title: params.title,
            source: params.body,
        });
        this.add(s);

        if (params.onLoad) this.coords.project$.runWhenReady(() => this.run(s));
    }

    add(s: Script) {
        this._scriptMap.set(s.id, s);
        this.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    run(s: string | Script) {
        if (typeof s === 'string')
            s = new Script(this.coords, {
                id: 'custom',
                title: 'Custom',
                source: s,
            });
        const resp = s.isAsynchronous ? s.runAsync() : s.run();

        return resp;
    }

    async runConnector(s: Script) {
        console.log('run connector', s);
        try {
            const f = await this.coords.filesystem$.pickFiles();
            if (!f) return;
            const params = { input: await f.text() };
            s.isAsynchronous ? s.runAsync(params) : s.run(params);
        } catch (e) {
            console.log(e);
            return;
        }
    }

    async openProject(pId: string) {
        let t = 0.1;
        while (!this.coords.worker$.isReady) {
            t *= 2;
            await this.coords.utils$.wait(t);
            if (t > 5) return;
        }

        const p = this.coords.project$.getProjectInList(pId);
        p?.select();
    }

    private async _hasProject() {
        let i = 0;
        while (!this.coords.dataset$.isReady) {
            await this.coords.utils$.wait(0.5);
            if (i++ > 10) return false;
        }
        return true;
    }

    private async _hasUser() {
        let i = 0;
        while (!this.coords.user$.current) {
            await this.coords.utils$.wait(0.5);
            if (i++ > 10) return false;
        }
        return true;
    }

    hitset(isPreview: boolean = false): ScriptHitset {
        return new ScriptHitset(this.coords, isPreview);
    }

    interface(tp: string) {
        return new ScriptInterface(this.coords, tp);
    }

    now() {
        return Temporal.Instant.fromEpochMilliseconds(Date.now());
    }

    log(...args: any[]): void {
        console.log(...args);
    }

    active() {
        const $s = this.coords.stage$;
        if (!$s.isDataset) return;
        const ls = this.coords.resultList$.activeList$.getValue().map((x) => x.scriptClass);
        const nH = this.hitset($s.isPreview);
        nH.add(ls);
        return nH;
    }

    dataset(isPreview = false) {
        const d$ = this.coords.dataset$;
        if (!d$.isReady) throw new Error('Premature call of dataset(); project is not ready');
        return (isPreview ? d$.preview : d$.main).scriptClass;
    }

    context(params?: { [tag: string]: any }) {
        return new ScriptContext(this.coords, params);
    }

    input() {
        return new ScriptInput(this.coords);
    }

    output(isBackup = false) {
        const out = new ScriptOutput(this.coords);
        out.isBackup = isBackup;
        return out;
    }

    xml() {
        return new ScriptXml(this.coords);
    }

    html() {
        return new ScriptHtml(this.coords);
    }

    search(arg1: string, arg2?: string | boolean, arg3?: boolean) {
        let field: string;
        let body: string;
        let isPreview: boolean;

        if (arg2 === undefined) {
            field = 'text';
            body = arg1;
            isPreview = false;
        } else if (arg3 !== undefined) {
            field = arg1;
            body = arg2 as string;
            isPreview = arg3;
        } else if (typeof arg2 === 'boolean') {
            isPreview = arg2 as boolean;
            field = 'text';
            body = arg1;
        } else if (typeof arg2 === 'string') {
            field = arg1;
            body = arg2;
            isPreview = false;
        } else {
            field = 'text';
            body = arg1;
            isPreview = false;
        }

        const p = new (field == 'year' ? SearchNumberParser : SearchStringParser)(
            this.coords,
            field,
            isPreview!,
        );

        const js = p.parse(body);
        const resp = this.run(js.interpretation) as ScriptHitset;

        if (resp) resp.payload = js.highlightLs;
        return resp;
    }

    searchArchive() {
        return new ScriptSearchArchive(this.coords);
    }

    batchManager() {
        return new ScriptBatchManager(this.coords);
    }

    subset(title: string | string[], isCreate = false) {
        let isSingle = false;
        if (!Array.isArray(title)) {
            isSingle = true;
            title = [title];
        }

        const respLs: (ScriptSubset | null)[] = [];
        title.forEach((t) => {
            t = t.trim();
            let v = this.coords.view$.get(t);
            if (!v) v = this.coords.view$.getByTitle(t);
            if (!v && isCreate) v = this.coords.view$.create({ title: t });
            v && respLs.push(v.scriptClass);
        });
        return isSingle ? respLs[0] || null : respLs;
    }

    attachment() {
        const att = new Attachment(this.coords, {
            targetId: '_new',
        });
        return att.scriptClass;
    }

    citation() {
        return new ScriptCitation(this.coords);
    }

    keyword(source: string | string[], isCreate = false) {
        let isSingle = false;
        if (!Array.isArray(source)) {
            isSingle = true;
            source = [source];
        }

        const respLs: (ScriptKeyword | null)[] = [];

        source.forEach((s) => {
            let k = this.coords.keyword$.getBySource(s);
            if (!k) {
                if (!isCreate) {
                    respLs.push(null);
                    return;
                }
                k = this.coords.keyword$.create({ regexp: s });
                this.coords.keyword$.update(k);
            }
            respLs.push(k.scriptClass);
        });
        return isSingle ? respLs[0] : respLs;
    }

    keywordGroup(title: string | string[], isCreate = false) {
        let isSingle = false;
        if (!Array.isArray(title)) {
            isSingle = true;
            title = [title];
        }
        const respLs: (ScriptKeywordGroup | null)[] = [];

        title.forEach((t) => {
            let kwg = this.coords.keyword$.getGroupByTitle(t);
            if (!kwg) {
                if (!isCreate) {
                    respLs.push(null);
                    return;
                }
                kwg = new KeywordGroup(this.coords, { title: t });
                kwg.confirmCreate(
                    new StyleGroup(this.coords, {
                        style: {},
                    }),
                );
                respLs.push(kwg.scriptClass);
            }
        });
        return isSingle ? respLs[0] : respLs;
    }

    private _binaryGroup(type: BTTYPE, title: string, isCreate = false) {
        const btId = this.coords.binaryTree$.getTree(title.trim());
        if (btId) return btId.scriptClass;

        let bt = this.coords.binaryTree$.getByTitle(type, title) || null;
        if (bt) return bt.scriptClass;

        if (!bt) {
            if (!isCreate) return null;
            const templ = <BTPAR>this.coords.template$.get(type);
            bt = this.coords.binaryTree$.createBinaryTree(templ);
            bt.title = title;
            this.coords.binaryTree$.setTree(bt);
            bt.markForManifestStore();
        }

        return bt.scriptClass;
    }

    ratings(title: string, isCreate = false) {
        return this._binaryGroup('ratings', title, isCreate);
    }

    tags(title: string, isCreate = false) {
        return this._binaryGroup('tags', title, isCreate);
    }

    close() {
        this.coords.display$.closeDialog();
    }

    async prompt(prompt: string) {
        await this._hasUser();
        console.log('UUUUUUUU', this.coords.user$.current);
        const resp = await this.coords.firebase$.callFunction('prompt', { prompt });

        if (resp === 'ERROR') return console.log('GEMINI ERROR');
        // this.coords.ai$.processResponse(resp.data);
    }

    stage(stageId: STG | 'summary', tabId?: string) {
        if (stageId === 'summary') stageId = 'stats';
        const stg = this.coords.stage$.get(stageId);
        return stg && stg.scriptClass;
    }

    settings() {
        return new ScriptSettings(this.coords);
    }

    report(title?: string) {
        title = title?.trim();
        let r: Report;
        if (title) {
            r = new Report(this.coords, { title });
        } else {
            r = new Report(this.coords);
        }

        return r.scriptClass;
    }

    private _excludeSet = new Set<string>(['onStart', 'dialog', 'add', 'run', 'get', 'set']);

    private _prepareContext() {
        const methodNames = Object.getOwnPropertyNames(Object.getPrototypeOf(this));
        for (const methodName of methodNames) {
            if (methodName.startsWith('_') || this._excludeSet.has(methodName)) continue;
            const method = (this as any)[methodName];
            if (typeof method === 'function' && methodName !== 'constructor') {
                this._availableFunctions[methodName] = (...args: any[]) => method.apply(this, args);
            }
        }
    }

    save(title: string, data: string, onStart?: boolean) {
        const s = new Script(this.coords, {
            title: title,
            source: data,
            onStart: onStart,
        });
        this.add(s);
        return s;
    }

    delete(s: Script) {
        this._scriptMap.delete(s.id);
        this.markForManifestStore();
        this.coords.display$.markForCheck();
    }

    private _manifestStore() {
        this.coords.manifest$.store({
            type: 'script',
            id: `scriptLs_${this.coords.project$.currentId}`,
            valueMap: new Map<string, any[]>([['scriptLs', this.scriptLs.map((s) => s.data)]]),
        });
    }

    load3(params: any) {
        params?.scriptLs?.forEach((data: any) => {
            const s = new Script(this.coords, data);
            this._scriptMap.set(s.id, s);
        });
        this.refreshInLists();
    }

    onLoad() {
        this._scriptMap.forEach((s) => {
            s.onStart && (s.isAsynchronous ? s.runAsync() : s.run());
        });
    }

    public get hasScriptInRun() {
        return this._hasScriptInRun;
    }
    public set hasScriptInRun(value) {
        this._hasScriptInRun = value;
        if (!value) this.coords.display$.markForCheck();
    }

    get availableFunctions() {
        return this._availableFunctions;
    }

    connectionDialog() {
        this.coords.display$.dialog$.next({
            action: 'connection-script',
        });
    }
}
